import axios from "axios";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Footer from "./Footer";
import TopBar from "./Topbar";
import useConfig from "../hooks/useConfig";

import { notifyUser } from "../utils/notifyUser";

// Get backend URL from environment or use default
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PhoneVerificationPage = () => {
    const navigate = useNavigate();
    const [phone, setPhone] = useState("");
    const [otp, setOtp] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [otpVerified, setOtpVerified] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { getConfig, loading: configLoading } = useConfig();
    const phoneVerificationEnabled =
        getConfig("ENABLE_PHONE_VERIFICATION") === "true";

    // Phone number regex for validation
    const phoneRegex = /^\d{10}$/;

    const handleSendOtp = async () => {
        if(!phoneRegex.test(phone.replace(/\D/g, ""))) {
            notifyUser(null, "error", "Please enter a valid 10-digit phone number.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        // If phone verification is disabled, automatically set as verified
        if(!phoneVerificationEnabled) {
            setOtpVerified(true);
            notifyUser(null, "success", "Phone verification is disabled. Your phone number has been accepted.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setOtpLoading(true);
        try {
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            const res = await axios.post(`${BACKEND_URL}/api/send-otp`, {
                phoneNumber: phone.replace(/\D/g, ""),
                email: user.email,
            });

            if(res.data.disabled || res.data.verified) {
                setOtpVerified(true);
                notifyUser(null, "success", "Phone verification is disabled. Your phone number has been accepted.", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                setOtpSent(true);
                notifyUser(null, "success", "OTP sent to your phone.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } catch(err) {
            console.error("Send OTP failed:", err);
            if(err.response?.data?.alreadyInUse) {
                notifyUser(null, "error", "Phone number is already in use by another account. Please use a different number.", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                notifyUser(null, "error", "Failed to send OTP.", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } finally {
            setOtpLoading(false);
        }
    };

    const handleVerifyOtp = async () => {
        if(!otp) {
            notifyUser(null, "error", "Enter the OTP received on your phone.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        // If phone verification is disabled, automatically set as verified
        if(!phoneVerificationEnabled) {
            setOtpVerified(true);
            notifyUser(null, "success", "Phone verification is disabled. Your phone number has been accepted.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setOtpLoading(true);
        try {
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            const res = await axios.post(`${BACKEND_URL}/api/verify-otp`, {
                phoneNumber: phone.replace(/\D/g, ""),
                otp,
                email: user.email,
            });

            if(res.data.disabled) {
                setOtpVerified(true);
                notifyUser(null, "success", "Phone verification is disabled. Your phone number has been accepted.", "toast", null, null, null, null, null, false, false, "top-center");
            } else {
                setOtpVerified(true);
                notifyUser(null, "success", "Phone number verified!", "toast", null, null, null, null, null, false, false, "top-center");
            }
        } catch(err) {
            console.error("OTP verification failed:", err);
            notifyUser(null, "error", "Invalid or expired OTP.", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setOtpLoading(false);
        }
    };

    const handleContinue = async () => {
        if(!otpVerified) {
            notifyUser(null, "error", "Please verify your phone number before continuing.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        setIsSubmitting(true);
        try {
            // Update user phone number in backend
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            await axios.post(`${BACKEND_URL}/api/update-phone`, {
                email: user.email,
                phoneNumber: phone.replace(/\D/g, ""),
            });

            notifyUser(null, "success", "Phone number updated successfully!", "toast", null, null, null, null, null, false, false, "top-center");

            // Navigate to investment limits page (step 5 of 5)
            setTimeout(() => navigate("/investment-limits"), 1000);
        } catch(error) {
            console.error("Error updating phone:", error);
            notifyUser(null, "error", "Failed to update phone number. Please try again.", "toast", null, null, null, null, null, false, false, "top-center");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Format phone number for display
    const formatPhoneNumber = (value) => {
        const digits = value.replace(/\D/g, "");
        if(digits.length <= 3) return digits;
        if(digits.length <= 6)
            return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(
            6,
            10,
        )}`;
    };

    const handlePhoneChange = (e) => {
        const formatted = formatPhoneNumber(e.target.value);
        setPhone(formatted);

        // Reset verification if phone number changes
        if(otpVerified || otpSent) {
            setOtpVerified(false);
            setOtpSent(false);
            setOtp("");
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-white">
            <TopBar />

            {/* Progress Bar */}
            <div className="bg-gray-100 p-4">
                <div className="max-w-6xl mx-auto">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-600">
                            Step 4 of 5
                        </span>
                        <span className="text-sm text-gray-600">80%</span>
                    </div>
                    <div className="w-full bg-gray-300 rounded-full h-2">
                        <div
                            className="bg-navy h-2 rounded-full transition-all duration-300"
                            style={{ width: "80%" }}
                        ></div>
                    </div>
                </div>
            </div>

            <div className="flex-grow flex items-center justify-center py-8 px-4">
                <div className="max-w-2xl w-full">
                    <div className="bg-white p-8 rounded-xl shadow-lg">
                        <div className="text-center mb-8">
                            <h1 className="text-3xl font-bold text-navy mb-4">
                                Verify Your Phone Number
                            </h1>
                            <p className="text-gray-600">
                                Please provide and verify your phone number to
                                secure your account and enable important
                                notifications.
                            </p>
                        </div>

                        <div className="space-y-6">
                            {/* Phone Number Input */}
                            <div>
                                <label className="block text-sm font-medium text-navy mb-2">
                                    Phone Number *
                                </label>
                                <input
                                    type="tel"
                                    placeholder="(555) 123-4567"
                                    value={phone}
                                    onChange={handlePhoneChange}
                                    disabled={otpVerified}
                                    className="w-full p-3 border border-navy rounded-xl font-poppins focus:ring-2 focus:ring-navy focus:border-transparent"
                                    maxLength="14"
                                />

                                {otpVerified && (
                                    <div className="mt-2 flex items-center text-green-600">
                                        <span className="text-sm font-medium">
                                            ✓ Phone number verified
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Send OTP Button */}
                            {!otpSent && !otpVerified && (
                                <button
                                    onClick={handleSendOtp}
                                    disabled={
                                        !phone || otpLoading || configLoading
                                    }
                                    className="w-full bg-navy text-white py-3 px-4 rounded-xl font-medium hover:bg-navy-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {otpLoading
                                        ? "Sending..."
                                        : "Send Verification Code"}
                                </button>
                            )}

                            {/* OTP Input and Verification */}
                            {otpSent && !otpVerified && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-navy mb-2">
                                            Verification Code
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Enter 6-digit code"
                                            value={otp}
                                            onChange={(e) =>
                                                setOtp(
                                                    e.target.value
                                                        .replace(/\D/g, "")
                                                        .slice(0, 6),
                                                )
                                            }
                                            className="w-full p-3 border border-navy rounded-xl font-poppins text-center text-lg tracking-widest"
                                            maxLength="6"
                                        />
                                    </div>

                                    <div className="flex gap-3">
                                        <button
                                            onClick={handleVerifyOtp}
                                            disabled={
                                                otp.length !== 6 || otpLoading
                                            }
                                            className="flex-1 bg-green-600 text-white py-3 px-4 rounded-xl font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            {otpLoading
                                                ? "Verifying..."
                                                : "Verify Code"}
                                        </button>

                                        <button
                                            onClick={() => {
                                                setOtpSent(false);
                                                setOtp("");
                                            }}
                                            className="px-4 py-3 text-navy border border-navy rounded-xl font-medium hover:bg-gray-50 transition-colors"
                                        >
                                            Resend
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Continue Button */}
                            {otpVerified && (
                                <button
                                    onClick={handleContinue}
                                    disabled={isSubmitting}
                                    className="w-full bg-navy text-white py-3 px-4 rounded-xl font-medium hover:bg-navy-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {isSubmitting ? "Updating..." : "Continue"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default PhoneVerificationPage;
